"use client";
import { useQueryParams, useFilters } from "@/hooks";
import {
  IndexCard,
  LocaleIsoCode,
  SortingFunction,
  SortingObject,
} from "@/types";
import { Filters, TreeNode } from "@/types/General";
import {
  createContext,
  FC,
  PropsWithChildren,
  useState,
  useEffect,
  useCallback,
  Suspense,
} from "react";
import { useTransition } from "react";
import {
  createTreeFilter,
  retrieveIndexCards,
  getSearchFieldsByVariant,
  SearchField,
} from "@/helpers/api/builder";
import { usePathname, useSearchParams } from "next/navigation";
import { createSortingMapping, sortCards } from "@/helpers";
import extractLocaleAndUrl from "@/helpers/extractLocaleAndUrl";
import { Spinner } from "@/icons";
import {
  filterCardsByQuery,
  parseFilterQueryParams,
} from "./IndexContextBlock.builder";

type IndexCardsContextType = {
  cards: { id: string; data: IndexCard }[];
  displayedCards: { id: string; data: IndexCard }[];
  hiddenCards: { id: string; data: IndexCard }[];
  filters: {
    label: string;
    name: string;
    categories: TreeNode[];
  }[];
  selectedFilters: Filters;
  isLoading: boolean;
  numberOfResults: number;
  page: number;
  numberOfPaginatedCards: number;
  searchExpression: string;
  activeSortDescriptor: SortingObject | null;
  onGoToFirstPage: () => void;
  onGoToLastPage: () => void;
  onGoToNextPage: () => void;
  onFilter: (newFilters: Filters) => void;
  onSort: (descriptor: SortingObject | null) => void;
};
export const IndexCardsContext = createContext<IndexCardsContextType>({
  cards: [],
  displayedCards: [],
  hiddenCards: [],
  filters: [],
  selectedFilters: [],
  isLoading: false,
  numberOfResults: 0,
  numberOfPaginatedCards: 10,
  page: 1,
  searchExpression: "",
  activeSortDescriptor: null,
  onGoToFirstPage: () => {},
  onGoToLastPage: () => {},
  onGoToNextPage: () => {},
  onFilter: () => {},
  onSort: () => {},
});

export type IndexCardsContextBlockProps = {
  paginationType: "infinite" | "expandable";
  numberOfPaginatedCards?: number;
  initialSorting: SortingObject;
  cards: { id: string; data: IndexCard }[];
  filters: { id: string; data: IndexCard }[];
  searchExpression?: string;
  variant:
    | "Resource"
    | "Event"
    | "Use Case"
    | "Integration"
    | "Product"
    | "Newsroom"
    | "CVE";
  locale: LocaleIsoCode;
};
const IndexCardsBlockInner: FC<
  PropsWithChildren<IndexCardsContextBlockProps>
> = ({
  children,
  cards: initialCards,
  numberOfPaginatedCards = 10,
  initialSorting,
  filters,
  searchExpression,
  variant,
  locale,
}) => {
  const searchFields = getSearchFieldsByVariant(variant);
  const { url: path } = extractLocaleAndUrl(
    usePathname().split("/").filter(Boolean),
  );

  const searchParams = useSearchParams();
  const searchExpressionQuery = searchParams.get("searchTerm")?.toLowerCase();
  const filterQueryParam = parseFilterQueryParams(
    filters,
    Object.fromEntries(searchParams.entries()),
  );

  const [cards, setCards] = useState(initialCards);
  const numberOfResults = cards.length;
  const lastPage = Math.ceil(numberOfResults / numberOfPaginatedCards);

  const initialSortingObject: SortingObject | null =
    initialSorting && "field" in initialSorting && "order" in initialSorting
      ? initialSorting
      : null;
  const [activeSortDescriptor, setActiveSortDescriptor] =
    useState<SortingObject | null>(initialSortingObject);
  const sortingFunction = activeSortDescriptor
    ? createSortingMapping([activeSortDescriptor])?.[0]
    : ((() => 0) as SortingFunction);

  const sortedCards = sortCards(cards, sortingFunction);

  const [page, setPage] = useState(1);

  const initialFiltersArray = Object.entries(filterQueryParam).map(
    ([key, value]) => ({
      name: key,
      paths: Array.from(
        new Set(
          filters.flatMap(({ data }) =>
            (data.categorization ?? [])
              .filter(({ parameterName }) => parameterName === key)
              .flatMap(({ categories }) =>
                (categories ?? []).filter((category) =>
                  value.some((filterCategory) => category === filterCategory),
                ),
              ),
          ),
        ),
      ),
    }),
  );

  const { selectedFilters, setSelectedFilters } =
    useFilters(initialFiltersArray);

  const [isLoading, startLoadingTransition] = useTransition();

  const setQueryParams = useQueryParams(filters);

  const onQuery = useCallback(
    (
      filters: Filters,
      searchExpression: string | undefined,
      searchFields: SearchField[],
    ) => {
      setPage(1);
      setCards([]);
      setQueryParams(filters, searchExpression);

      startLoadingTransition(async () => {
        const { cards } = await retrieveIndexCards(
          path,
          locale,
          filters,
          searchExpression,
          undefined,
          searchFields,
        );

        startLoadingTransition(() => {
          setCards(cards);
        });
      });
    },
    [locale, path, setQueryParams],
  );

  useEffect(() => {
    if (
      searchExpression != null &&
      searchExpression.toLowerCase() !== searchExpressionQuery?.toLowerCase()
    ) {
      // We intentionally refresh index results when incoming search term and URL differ.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      onQuery(selectedFilters, searchExpression, searchFields);
    }
  }, [
    selectedFilters,
    onQuery,
    searchExpression,
    searchExpressionQuery,
    searchFields,
  ]);

  const onGoToFirstPage = () => {
    setPage(1);
  };

  const onGoToLastPage = () => {
    setPage(lastPage);
  };

  const onGoToNextPage = () => {
    setPage((currentValue) => currentValue + 1);
  };

  const onFilter = (newFilters: Filters) => {
    setSelectedFilters(newFilters);
    onQuery(newFilters, searchExpression, searchFields);
  };

  const onSort = (descriptor: SortingObject | null) => {
    setActiveSortDescriptor(descriptor ?? null);
  };

  return (
    <IndexCardsContext
      value={{
        cards,
        displayedCards: sortedCards.slice(0, page * numberOfPaginatedCards),
        hiddenCards: sortedCards.slice(page * numberOfPaginatedCards),
        numberOfResults: sortedCards.length,
        filters: createTreeFilter(filters),
        selectedFilters,
        isLoading,
        numberOfPaginatedCards,
        page,
        searchExpression: (searchExpression || searchExpressionQuery) ?? "",
        activeSortDescriptor,
        onGoToFirstPage,
        onGoToLastPage,
        onGoToNextPage,
        onFilter,
        onSort,
      }}
    >
      {children}
    </IndexCardsContext>
  );
};

export const IndexCardsContextBlock: FC<
  PropsWithChildren<IndexCardsContextBlockProps>
> = (props) => {
  return (
    <Suspense
      fallback={
        <div className="mx-auto mt-8 w-fit animate-spin">
          <Spinner />
        </div>
      }
    >
      <IndexCardsBlockInner {...props} />
    </Suspense>
  );
};
